import TitrePage from "../components/charte/TitrePage";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getUserdetails, blockUser, unblockUser, corrigerSolde, nouvelleCle, messageServeur } from "../utilities/userService";
import { getUser } from "../utilities/adminservice";
import locationApi from "../utilities/locationApi";
import CommonDialog from "../commondialog/confirm";
import { useDispatch, useSelector } from "react-redux";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import { useNavigate } from "react-router-dom";
import store from "../redux/store";
import { AIDE_RESEAU } from "../aide/textesAide";

/* ── Design tokens ── */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:          'var(--c-bg)',
  card:        'var(--c-surface)',
  border:      'var(--c-border)',
  borderAccent:'#1F1E1B',
  accent:      '#1F1E1B',
  accentLight: '#57534A',
  accentMuted: 'rgba(31,30,27,0.07)',
  text:        '#1F1E1B',
  textSub:     '#57534A',
  textMuted:   '#57534A',
  success:     '#356A0F',
  warning:     '#7C5600',
  error:       '#A23A0D',
} as const;

// Seuil d'alerte affiché (Z4-20) : aucun rejet automatique n'existe côté
// serveur, c'est un repère pour l'admin.
const REJECT_THRESHOLD = 25;

/* ── Avatar (même logique que user_location.tsx) ── */
const AVATAR_COLORS = ['#1F1E1B', '#356A0F', '#7C5600'];
function emailHash(email: string): number {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function Avatar({ email }: { email: string }) {
  const color = AVATAR_COLORS[emailHash(email) % 3];
  return (
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: color + '20', border: `1.5px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, color, lineHeight: 1 }}>
        {email.slice(0, 2).toUpperCase()}
      </Typography>
    </Box>
  );
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  return (local || '').slice(0, 3) + '***@' + (domain || '');
}

/* ── Risk score ── */
function getRisk(rejRate: number): { label: string; color: string; barPct: number } {
  if (rejRate > 35) return { label: 'Critique', color: C.error,   barPct: Math.min(70 + (rejRate - 35) * 2, 100) };
  if (rejRate >= 20) return { label: 'Moyen',   color: C.warning, barPct: 40 + ((rejRate - 20) / 15) * 25 };
  return               { label: 'Faible',   color: C.success, barPct: Math.max((rejRate / 20) * 30, 2) };
}

/* ── Status helpers ── */
type StatusKey = 'actif' | 'suspendu' | 'supprime';
function getStatus(rawStatus: string): StatusKey {
  const s = (rawStatus || '').toLowerCase();
  if (s === 'suspended' || s === 'deleted' || s === 'blocked') return 'suspendu';
  if (s === 'archived')                       return 'supprime';
  return 'actif';
}
const STATUS_LABEL: Record<StatusKey, { label: string; color: string; bg: string; border: string }> = {
  actif:      { label: 'Actif',      color: C.success, bg: 'rgba(53,106,15,0.10)', border: 'rgba(53,106,15,0.2)' },
  suspendu:   { label: 'Suspendu',   color: C.error,   bg: 'rgba(162,58,13,0.10)', border: 'rgba(162,58,13,0.2)' },
  supprime:   { label: 'Supprimé',   color: C.textMuted, bg: 'rgba(31,30,27,0.04)', border: C.border },
};

/* ── KPI card ── */
function KpiCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', p: 2.5 }}>
      <Typography sx={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
    </Box>
  );
}

/* ── Action button ── */
function ActionBtn({ label, color, onClick, disabled }: { label: string; color?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <Button size="small" disabled={disabled} onClick={onClick} sx={{
      fontSize: 10, fontWeight: 700, textTransform: 'none', px: 1.25, py: 0.4, borderRadius: '5px', minWidth: 0,
      border: `1px solid ${color ? color + '40' : C.border}`,
      color: color || C.textMuted,
      background: color ? color + '10' : 'transparent',
      '&:hover': { background: color ? color + '20' : 'rgba(31,30,27,0.04)' },
      '&.Mui-disabled': { opacity: 0.35, color: C.textMuted },
    }}>
      {label}
    </Button>
  );
}

/* ── Types ── */
type FilterKey = 'all' | 'actif' | 'suspendu' | 'risque' | 'inactif30j';

interface UserRow {
  userId: string;
  email: string;
  first_name: string;
  last_name: string;
  account_status: string;
  totalPois: number;
  approvedPois: number;
  rejectedPois: number;
  city?: string;
  firstPoi?: Date;
  lastPoi?: Date;
  tips?: number;
}

interface PendingAction {
  type: 'bloquer' | 'debloquer' | 'bulkBloquer';
  userId?: string;
  userIds?: string[];
  label: string;
}

/* ── Main component ── */
export default function User_Access() {
  const [users, setUsers]             = useState<UserRow[]>([]);
  const [poisByEmail, setPoisByEmail] = useState<Record<string, any>>({});
  const [page, setPage]               = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter]           = useState<FilterKey>('all');
  const [selected, setSelected]       = useState<string[]>([]);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [accessrestriction_write, setAccessrestriction_write] = useState(false);
  // Correction manuelle d'un solde (décision D6) : montant signé et motif obligatoires.
  const [correction, setCorrection] = useState<{ row: any; montant: string; motif: string; cle: string } | null>(null);
  const [correctionConfirmee, setCorrectionConfirmee] = useState(false);

  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  /* ── Fetch ── */
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    dispatch(loaderOn());
    try {
      // Role permissions
      // Droits lus à part : leur échec ne doit pas empêcher la liste de charger.
      try {
        const roleRes: any = await getUser();
        setAccessrestriction_write(!!roleRes?.role_type?.accessrestriction_write);
      } catch (_) {
        dispatch(openSnackbar("Droits de l'administrateur illisibles : actions de modification masquées", "red"));
      }

      // Users
      const backendData = await getUserdetails();
      const userData: UserRow[] = Array.isArray(backendData)
        ? backendData
            .filter((u: any) => !u.role)
            .map((u: any) => ({
              userId:         u.userId ?? u.user_id,   // /user/all renvoie user_id
              email:          u.email || '',
              first_name:     u.first_name || '',
              last_name:      u.last_name  || '',
              account_status: u.account_status || u.status || 'active',
              tips:           Number(u.token_avaliable ?? 0),
              totalPois: 0, approvedPois: 0, rejectedPois: 0,
            }))
        : [];
      setUsers(userData);

      // Agrégat par Spotter calculé par le serveur, au lieu de charger toute
      // la collection des POI (Z4-23).
      const agregat = await locationApi.get("/location/statistiques/spotters");
      const byEmail: Record<string, any> = {};
      (Array.isArray(agregat.data) ? agregat.data : []).forEach((l: any) => {
        if (l.email) byEmail[l.email] = l;
      });
      setPoisByEmail(byEmail);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    dispatch(loaderOff());
  };

  /* ── Merge users with POI data ── */
  const enrichedUsers = useMemo((): (UserRow & {
    status: StatusKey; rejRate: number; risk: ReturnType<typeof getRisk>;
  })[] => {
    const now = Date.now();
    return users.map(u => {
      const a = poisByEmail[u.email];
      const totalPois    = Number(a?.total ?? 0);
      const approvedPois = Number(a?.approuves ?? 0);
      const rejectedPois = Number(a?.rejetes ?? 0);
      const rejRate      = totalPois > 0 ? (rejectedPois / totalPois) * 100 : 0;
      const firstPoi = a?.premier ? new Date(a.premier) : undefined;
      const lastPoi  = a?.dernier ? new Date(a.dernier) : undefined;
      const city     = a?.ville || undefined;
      void now;

      return {
        ...u, totalPois, approvedPois, rejectedPois, city, firstPoi, lastPoi,
        status: getStatus(u.account_status),
        rejRate,
        risk: getRisk(rejRate),
      };
    });
  }, [users, poisByEmail]);

  /* ── KPIs ── */
  const kpis = useMemo(() => ({
    actifs:     enrichedUsers.filter(u => u.status === 'actif').length,
    suspendus:  enrichedUsers.filter(u => u.status === 'suspendu').length,
    supprimes:  enrichedUsers.filter(u => u.status === 'supprime').length,
  }), [enrichedUsers]);

  /* ── Filter + search ── */
  const filteredUsers = useMemo(() => {
    const d30ago = new Date(Date.now() - 30 * 86400000);
    let list = enrichedUsers;
    if (filter === 'actif')      list = list.filter(u => u.status === 'actif');
    if (filter === 'suspendu')   list = list.filter(u => u.status === 'suspendu');
    if (filter === 'risque')     list = list.filter(u => u.rejRate > REJECT_THRESHOLD);
    if (filter === 'inactif30j') list = list.filter(u => !u.lastPoi || u.lastPoi < d30ago);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u => u.email.toLowerCase().includes(q) || u.first_name.toLowerCase().includes(q));
    }
    return list;
  }, [enrichedUsers, filter, searchQuery]);

  const sliced = filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  /* ── Selection ── */
  const toggleSelect = (userId: string) =>
    setSelected(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);

  /* ── Actions ── */
  const executeAction = async (action: PendingAction) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    // Bloquer est une décision tracée : motif obligatoire (journal du compte).
    let motif = "";
    if (action.type === 'bloquer' || action.type === 'bulkBloquer') {
      motif = (window.prompt("Motif du blocage (obligatoire, conservé au journal) :") || "").trim();
      if (!motif) { dispatch(openSnackbar("Blocage annulé : motif obligatoire.", "red")); return; }
    }
    dispatch(loaderOn());
    try {
      if (action.type === 'bloquer' && action.userId) {
        await blockUser(action.userId, motif);
      } else if (action.type === 'debloquer' && action.userId) {
        await unblockUser(action.userId);
      } else if (action.type === 'bulkBloquer' && action.userIds) {
        for (const uid of action.userIds) {
          await blockUser(uid, motif);
        }
        setSelected([]);
      }
      await fetchAll();
    } catch (err) {
      console.error('Action error:', err);
      dispatch(openSnackbar("Échec du blocage ou du déblocage : action refusée par le serveur.", "red"));
    }
  };

  const confirm = (action: PendingAction) => setPendingAction(action);

  const montantCorrection = correction ? Math.trunc(Number(correction.montant)) : 0;
  const correctionValide = !!correction && Number.isFinite(montantCorrection) && montantCorrection !== 0
    && correction.motif.trim() !== '' && (correction.row.tips ?? 0) + montantCorrection >= 0;

  const executerCorrection = async () => {
    if (!correction) return;
    setCorrectionConfirmee(false);
    dispatch(loaderOn());
    try {
      // Même clé tant que le formulaire est ouvert : une relance après une
      // erreur ne corrige pas deux fois.
      await corrigerSolde(correction.row.userId, montantCorrection, correction.motif.trim(), correction.cle);
      dispatch(openSnackbar('Solde corrigé, journalisé, Spotter notifié.', 'green'));
      setCorrection(null);
      await fetchAll();
    } catch (e: any) {
      dispatch(openSnackbar(messageServeur(e, 'Correction refusée par le serveur.'), 'red'));
    }
    dispatch(loaderOff());
  };

  const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
    { key: 'all',        label: 'Tous' },
    { key: 'actif',      label: 'Actifs' },
    { key: 'suspendu',   label: 'Suspendus' },
    { key: 'risque',     label: 'Score risque élevé' },
    { key: 'inactif30j', label: 'Inactifs 30j' },
  ];

  const fmtDate = (d?: Date) => d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: C.bg }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <TitrePage domaine="reseau" aide={AIDE_RESEAU.acces}>Restrictions d'accès</TitrePage>
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>
            Repérer les Spotters à risque, bloquer ou débloquer un compte, corriger un solde.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {selected.length > 0 && (
            <Button onClick={() => confirm({ type: 'bulkBloquer', userIds: selected, label: `Bloquer ${selected.length} Spotter(s) ?` })}
              disabled={!accessrestriction_write}
              sx={{ fontSize: 12, fontWeight: 700, textTransform: 'none', px: 2, py: 0.75, borderRadius: '7px',
                background: 'rgba(162,58,13,0.12)', color: C.error, border: '1px solid rgba(162,58,13,0.25)',
                '&:hover': { background: 'rgba(162,58,13,0.2)' },
                '&.Mui-disabled': { opacity: 0.4, color: C.error },
              }}>
              Bloquer sélection ({selected.length})
            </Button>
          )}
          <TextField
            placeholder="Rechercher..." value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
            variant="outlined" size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.textMuted, fontSize: 18 }} /></InputAdornment> }}
            sx={{ width: 220,
              '& .MuiOutlinedInput-root': {
                background: C.card, color: C.text, borderRadius: '7px',
                '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: C.borderAccent },
                '&.Mui-focused fieldset': { borderColor: C.accentLight },
              },
            }}
          />
        </Box>
      </Box>

      {/* 4 KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        <KpiCard label="Comptes actifs"  value={kpis.actifs}     color={C.success} />
        <KpiCard label="Suspendus"       value={kpis.suspendus}  color={C.error}   />
        <KpiCard label="Supprimés"       value={kpis.supprimes}  color={C.textMuted} />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
        {FILTER_OPTIONS.map(({ key, label }) => (
          <Box key={key} onClick={() => { setFilter(key); setPage(0); }} sx={{
            px: 2, py: 0.6, borderRadius: '7px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            border: `1px solid ${filter === key ? C.accentLight : 'rgba(31,30,27,0.08)'}`,
            background: filter === key ? C.accentMuted : 'transparent',
            color: filter === key ? C.accentLight : C.textMuted,
            transition: 'all 0.15s ease', userSelect: 'none',
          }}>
            {label}
          </Box>
        ))}
      </Box>

      {/* Table */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ background: C.card, borderBottom: `1px solid ${C.border}`, width: 48 }} />
                {['Spotter','Soumissions','Taux de rejet','Score risque','Statut','Actions'].map(col => (
                  <TableCell key={col} sx={{
                    background: C.card, color: C.accentLight, fontWeight: 700,
                    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em',
                    borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                  }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sliced.map(row => {
                const st = STATUS_LABEL[row.status];
                const isSelected = selected.includes(row.userId);
                return (
                  <TableRow key={row.userId} sx={{
                    background: isSelected ? 'rgba(31,30,27,0.05)' : 'rgba(31,30,27,0.01)',
                    '&:hover': { background: 'rgba(31,30,27,0.04)' },
                    '& .MuiTableCell-root': { borderBottom: `1px solid ${C.border}`, py: 1.25 },
                  }}>
                    {/* Checkbox */}
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleSelect(row.userId)}
                        disabled={!accessrestriction_write}
                        size="small"
                        sx={{ color: C.textMuted, '&.Mui-checked': { color: C.accentLight } }}
                      />
                    </TableCell>

                    {/* Collecteur */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar email={row.email} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                            {maskEmail(row.email)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: C.textMuted }}>
                            {row.city ? `${row.city} · ` : ''}{fmtDate(row.firstPoi)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Soumissions */}
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>{row.totalPois}</Typography>
                      {row.totalPois > 0 && (
                        <Typography sx={{ fontSize: 10, color: C.success }}>
                          {Math.round((row.approvedPois / row.totalPois) * 100)}% validés
                        </Typography>
                      )}
                    </TableCell>

                    {/* Taux de rejet */}
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: row.rejRate > REJECT_THRESHOLD ? C.error : row.rejRate >= 20 ? C.warning : C.success }}>
                        {row.totalPois > 0 ? `${Math.round(row.rejRate)}%` : '—'}
                      </Typography>
                      {row.rejRate > REJECT_THRESHOLD && (
                        <Typography sx={{ fontSize: 9, color: C.error, fontWeight: 600 }}>Seuil dépassé</Typography>
                      )}
                    </TableCell>

                    {/* Score risque */}
                    <TableCell sx={{ minWidth: 140 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: row.risk.color, minWidth: 52 }}>
                          {row.risk.label}
                        </Typography>
                        <Box sx={{ flex: 1, height: 4, borderRadius: '2px', background: 'rgba(31,30,27,0.06)', overflow: 'hidden' }}>
                          <Box sx={{ width: `${row.risk.barPct}%`, height: '100%', borderRadius: '2px', background: row.risk.color, transition: 'width 0.3s ease' }} />
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Statut */}
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', px: 1.25, py: 0.4, borderRadius: '5px', background: st.bg, border: `1px solid ${st.border}` }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: st.color }}>{st.label}</Typography>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {row.status === 'actif' ? (
                          <ActionBtn label="Bloquer" color={C.error} disabled={!accessrestriction_write}
                            onClick={() => confirm({ type: 'bloquer', userId: row.userId, label: 'Bloquer cet utilisateur ?' })} />
                        ) : row.status !== 'supprime' ? (
                          <ActionBtn label="Débloquer" color={C.success} disabled={!accessrestriction_write}
                            onClick={() => confirm({ type: 'debloquer', userId: row.userId, label: 'Débloquer cet utilisateur ?' })} />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: C.textMuted, fontStyle: 'italic' }}>Archivé</Typography>
                        )}
                        {row.status !== 'supprime' && (
                          <ActionBtn label="Corriger le solde" color={C.accentLight} disabled={!accessrestriction_write}
                            onClick={() => setCorrection({ row, montant: '', motif: '', cle: nouvelleCle() })} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sliced.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: C.textMuted, borderBottom: 'none' }}>
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
          sx={{ color: C.textSub, borderTop: `1px solid ${C.border}` }}
        />

        {/* Légende */}
        <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap', borderTop: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 11, color: C.textMuted }}>
            <Box component="span" sx={{ fontWeight: 700, color: C.success }}>Actif</Box> — accès complet à l'application
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.textMuted }}>
            <Box component="span" sx={{ fontWeight: 700, color: C.error }}>Suspendu</Box> — accès total bloqué
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.textMuted }}>
            Seuil d'alerte (taux de rejet) : <Box component="span" sx={{ fontWeight: 700, color: C.warning }}>{REJECT_THRESHOLD}%</Box>
          </Typography>
        </Box>
      </Box>

      {/* Correction de solde (D6) */}
      <Dialog open={correction !== null && !correctionConfirmee} onClose={() => setCorrection(null)} fullWidth maxWidth="sm">
        <DialogTitle>Corriger le solde</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {correction?.row.email} · solde actuel : <strong>{(correction?.row.tips ?? 0).toLocaleString('fr-FR')} Tips</strong>
          </Typography>
          <TextField fullWidth type="number" label="Montant en Tips (positif pour créditer, négatif pour retirer)"
            value={correction?.montant ?? ''} sx={{ mb: 2 }}
            onChange={e => correction && setCorrection({ ...correction, montant: e.target.value })} />
          <TextField fullWidth label="Motif (obligatoire, conservé au journal)"
            value={correction?.motif ?? ''}
            onChange={e => correction && setCorrection({ ...correction, motif: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrection(null)}>Annuler</Button>
          <Button variant="contained" disabled={!correctionValide} onClick={() => setCorrectionConfirmee(true)}>Continuer</Button>
        </DialogActions>
      </Dialog>
      <CommonDialog
        open={correctionConfirmee}
        handleClose={() => setCorrectionConfirmee(false)}
        handleConfirm={executerCorrection}
        contentText={correction ? `${montantCorrection > 0 ? 'Créditer' : 'Retirer'} ${Math.abs(montantCorrection).toLocaleString('fr-FR')} Tips à ${correction.row.email} ? Nouveau solde : ${((correction.row.tips ?? 0) + montantCorrection).toLocaleString('fr-FR')} Tips. Le Spotter sera notifié.` : ''}
      />

      {/* Confirmation dialog */}
      <CommonDialog
        open={pendingAction !== null}
        handleClose={() => setPendingAction(null)}
        handleConfirm={() => {
          if (pendingAction) executeAction(pendingAction);
          setPendingAction(null);
        }}
        contentText={pendingAction?.label || ''}
      />
    </Box>
  );
}
